import Add16 from '@carbon/icons-react/es/add/16';
import { Menu } from 'antd';
import { orderBy } from 'lodash';
import React, { FC, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { IconShell } from 'components/atoms/icon-shell';
import { SpaceIcon } from 'components/atoms/space-icon';
import { ProjectTemplateSpace } from 'data/templates/projects';
import { updateSpaceInProjectTemplate } from 'data/templates/projects/actions';
import { MoreActions } from './more-actions';
import { SpaceTabMenuItem } from './space-tab-menu-item';
import { TitleContainer } from './title-container';
import { generateTabMenuKey } from './utils';

const { SubMenu } = Menu;

export const Container = styled.span`
  width: calc(100% - var(--spacing-05));

  .add-button,
  .more-actions {
    opacity: 0;
    transition: opacity 50ms ease-in;
  }
  .add-button:focus-within,
  .more-actions:focus-within {
    opacity: 1;
  }

  a {
    width: 100%;
  }

  &:hover {
    .add-button,
    .more-actions {
      opacity: 1;
    }
  }
`;

export const TitleContainerStyled = styled.div<{ activeState: boolean }>`
  width: 100%;
  display: inline-flex;
  align-items: center;
  color: ${({ activeState }) =>
    activeState ? 'var(--blue-60)' : 'var(--gray-50)'};
  position: relative;
  top: var(--spacing-02);

  svg,
  .ant-image {
    margin-right: var(--spacing-02);
    flex-shrink: 0;

    &-img {
      display: block;
    }
  }

  svg {
    width: var(--spacing-06);
    height: var(--spacing-06);
    position: relative;
    right: var(--spacing-01);
  }
`;

interface SpaceSubMenuItemtypes {
  space: ProjectTemplateSpace;
  setOpenKeys: (keys: string[]) => void;
  isPrivate: boolean;
}

export const SpaceSubMenuItem: FC<SpaceSubMenuItemtypes> = ({
  space,
  setOpenKeys,
  isPrivate,
  ...rest
}) => {
  const { spaceId, spaceTabs } = space;

  const [isEdit, setEdit] = useState(false);
  const dispatch = useDispatch();
  const { templateId } = useParams<{ templateId: string }>();
  const { spaceId: currentSpaceID } = useParams<{ spaceId: string }>();
  const { pathname } = useLocation();
  const isNewTab = pathname.includes('/tabs/new');

  const onEdit = () => setEdit(true);
  const cancelEdit = () => setEdit(false);
  const onUpdate = useCallback(
    (spaceName: string) => {
      if (spaceName) {
        dispatch(
          updateSpaceInProjectTemplate({
            space: {
              spaceName,
              spaceId: space.spaceId,
            },
          }),
        );
        setEdit(false);
      }
    },
    [dispatch, space.spaceId],
  );

  const renderContent = useCallback(() => {
    if (!spaceTabs.length) {
      return (
        <Menu.Item
          key={generateTabMenuKey(spaceId, 'new')}
          data-cy={`templates.spaces.${isPrivate ? 'private' : 'shared'}.${
            space.spaceName
          }.add_new`}
          {...rest}
        >
          <Container>
            <Link
              to={`/templates/projects/${
                templateId || 'new'
              }/spaces/${spaceId}/tabs/new`}
              type="link"
            >
              <TitleContainerStyled
                activeState={spaceId === Number(currentSpaceID) && isNewTab}
              >
                <Add16 />
                <FormattedMessage id="Add new" />
              </TitleContainerStyled>
            </Link>
          </Container>
        </Menu.Item>
      );
    }

    if (spaceTabs.length) {
      return (
        <>
          {orderBy(spaceTabs, 'createdAt', 'desc').map((spaceTab) => {
            return (
              <SpaceTabMenuItem
                key={generateTabMenuKey(spaceId, spaceTab.spaceTabId)}
                spaceTab={spaceTab}
                spaceId={spaceId}
                showNewTab={spaceTab.spaceTabId.toString() === 'new'}
                dataCy={`document_template.${spaceTab.spaceTabTitle}`}
              />
            );
          })}
          <Menu.Item
            key={generateTabMenuKey(spaceId, 'new')}
            data-cy={`templates.spaces.${isPrivate ? 'private' : 'shared'}.${
              space.spaceName
            }.add_new`}
            {...rest}
          >
            <Container>
              <Link
                to={`/templates/projects/${
                  templateId || 'new'
                }/spaces/${spaceId}/tabs/new`}
                type="link"
              >
                <TitleContainerStyled
                  activeState={spaceId === Number(currentSpaceID) && isNewTab}
                >
                  <Add16 />
                  <FormattedMessage id="Add new" />
                </TitleContainerStyled>
              </Link>
            </Container>
          </Menu.Item>
        </>
      );
    }

    return null;
  }, [
    spaceTabs,
    spaceId,
    isPrivate,
    space.spaceName,
    rest,
    templateId,
    currentSpaceID,
    isNewTab,
  ]);

  return (
    <SubMenu
      key={spaceId.toString()}
      title={
        <TitleContainer
          spaceId={spaceId}
          isEdit={isEdit}
          spaceName={space.spaceName}
          moreActions={
            <MoreActions
              spaceId={space.spaceId}
              spaceName={space.spaceName}
              onEdit={onEdit}
            />
          }
          space={space}
          dataCy={`space_${space.spaceName}`}
          onChange={onUpdate}
          onCancel={cancelEdit}
        />
      }
      icon={
        <IconShell
          size="small"
          icon={
            <IconShell
              size="small"
              icon={<SpaceIcon name={space.spaceName} />}
            />
          }
        />
      }
      {...rest}
    >
      {renderContent()}
    </SubMenu>
  );
};
